// Abweichende Korrespondenzperson beim Kunden (Kanzlei Polz, Mail
// „Korrespondenz mit MDT" vom 16.10.2026): Grundsätzlich wird nur mit dem
// Mandanten korrespondiert. Für die Ausnahme hält die Akte fest, mit wem und
// warum – sichtbar überall, wo Kanzlei-Mitarbeiter den Kunden kontaktieren.

export interface CorrespondenceFields {
  correspondenceName?: string | null;
  correspondenceRelation?: string | null;
  correspondenceEmail?: string | null;
  correspondencePhone?: string | null;
  correspondenceReason?: string | null;
}

export interface CorrespondenceNotice {
  /** „Erika Muster (Ehefrau)" */
  person: string;
  /** E-Mail und Telefon, soweit vorhanden. */
  contacts: string[];
  reason: string;
  /** Ein Grund ist Pflicht („dass und warum") – fehlt er, wird gewarnt. */
  reasonMissing: boolean;
}

/** Vorschläge für „Beziehung zum Kunden" (freie Eingabe bleibt möglich). */
export const CORRESPONDENCE_RELATIONS: readonly string[] = [
  "Ehepartner/in",
  "Lebenspartner/in",
  "Sohn/Tochter",
  "Vater/Mutter",
  "Geschwister",
  "Betreuer/in (gesetzlich)",
  "Bevollmächtigte/r",
  "Arbeitgeber/Fuhrparkleitung",
];

export const CORRESPONDENCE_FIELD_KEYS = [
  "correspondenceName",
  "correspondenceRelation",
  "correspondenceEmail",
  "correspondencePhone",
  "correspondenceReason",
] as const;

/**
 * Darf die Organisation den Vermerk pflegen und den Grund sehen? Kanzleien ja;
 * Werkstatt, Sachverständige, Versicherungen, Logistik-Beteiligte und
 * Privatkunden nein. Sie sehen nur, DASS und mit wem korrespondiert wird
 * (Kanzlei Polz 18.10.2026) – das Backend maskiert dort den Grund und bewahrt
 * alle Felder beim Speichern (fieldpolicy.go). Unbekannte Rollen wie in
 * canSeeClientSection: voller Zugriff.
 */
export function canManageCorrespondence(organizationRoles: readonly string[] | null | undefined): boolean {
  const joined = (organizationRoles ?? []).join(" ").toLowerCase();
  if (/anwalt|kanzlei/.test(joined)) return true;
  return !/autohaus|werkstatt|fuhrpark|flotte|sachverst|gutachter|versicher|abschlepp|mietwagen|autovermiet|rental|beteiligter|privatkunde|zeuge/.test(joined);
}

const text = (value: unknown): string => (typeof value === "string" ? value.trim() : "");

/** Ist eine abweichende Korrespondenzperson eingetragen? Maßgeblich ist der Name. */
export function hasCorrespondencePerson(client: CorrespondenceFields | null | undefined): boolean {
  return text(client?.correspondenceName) !== "";
}

/** Angaben ohne Namen sind unvollständig – der Name ist Pflicht, sobald etwas eingetragen ist. */
export function correspondenceNameMissing(client: CorrespondenceFields | null | undefined): boolean {
  if (hasCorrespondencePerson(client)) return false;
  return CORRESPONDENCE_FIELD_KEYS.some((key) => text(client?.[key]) !== "");
}

/** Anzeige-Daten für den Hinweis an der Kundenkarte, `null` ohne Korrespondenzperson. */
export function correspondenceNotice(client: CorrespondenceFields | null | undefined): CorrespondenceNotice | null {
  if (!hasCorrespondencePerson(client)) return null;
  const name = text(client?.correspondenceName);
  const relation = text(client?.correspondenceRelation);
  const reason = text(client?.correspondenceReason);
  return {
    person: relation ? `${name} (${relation})` : name,
    contacts: [text(client?.correspondenceEmail), text(client?.correspondencePhone)].filter(Boolean),
    reason,
    reasonMissing: reason === "",
  };
}

/** Kurzfassung für den zugeklappten Abschnitt im Kunden-Formular. */
export function correspondenceSummary(client: CorrespondenceFields | null | undefined): string {
  const notice = correspondenceNotice(client);
  if (!notice) return "";
  return [notice.person, notice.reason].filter(Boolean).join(" · ");
}

/** id und Korrespondenz-Felder eines Kunden – für den Abgleich lokaler Kundenlisten. */
export function pickCorrespondence<T extends CorrespondenceFields & { id?: unknown }>(client: T): CorrespondenceFields & { id?: unknown } {
  return Object.fromEntries([["id", client.id], ...CORRESPONDENCE_FIELD_KEYS.map((key) => [key, client[key] ?? null])]);
}

/** Anzeigename des Kunden: Firma, sonst Vor- und Nachname. */
export function clientDisplayName(client: { company?: string | null; firstName?: string | null; lastName?: string | null } | null | undefined): string {
  const company = text(client?.company);
  if (company) return company;
  return [text(client?.firstName), text(client?.lastName)].filter(Boolean).join(" ");
}

/** Empfänger für das E-Mail-Fenster, `null` ohne Korrespondenzperson oder E-Mail. */
export function correspondenceRecipient(client: CorrespondenceFields | null | undefined): { email: string; name: string } | null {
  if (!hasCorrespondencePerson(client)) return null;
  const email = text(client?.correspondenceEmail).toLowerCase();
  if (!email) return null;
  return { email, name: text(client?.correspondenceName) };
}
